import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsStateService } from '../../ajs-state.service';
import { MetadataAdapter } from '../../core/adapters/metadata.adapter';
import { states } from '../../core/application.states';
import {
	BeElementTypesMapping,
	DcCoreElementType,
	DcCoreModule,
} from '../../core/dc-core-types';
import { ProjectStorage } from '../../core/services/project.storage';
import { ProjectRequester } from '../../requesters';
import { ProjectAdapter } from '../project.adapter';
import {
	changeElementSelection,
	fetchElementTracesAndCreate,
	fetchElementTracesSuccess,
	fetchProjectDetailsSuccess,
	initProjectHome,
	navigateToCreateElementRoute,
	navigateToElementListRoute,
	navigateToProjectEditRoute,
	refreshProjectTraceList,
} from './home.actions';
import { HomeSelector } from './home.selector';

@Injectable()
export class HomeEffects {
	private readonly elementModuleMapping: {
		[key: string]: DcCoreModule;
	} = {
		[DcCoreElementType.CONNECTOR]: 'generics',
		[DcCoreElementType.DATABLOCK]: 'generics',
		[DcCoreElementType.DATASOURCE]: 'generics',
		[DcCoreElementType.ENTITY]: 'generics',
		[DcCoreElementType.DEPOT]: 'generics',
		[DcCoreElementType.EXPORT_TEMPLATE]: 'generics',
		[DcCoreElementType.COLUMN]: 'generics',
		[DcCoreElementType.EXPOSITION]: 'handle',
		[DcCoreElementType.DASHBOARD]: 'handle',
		[DcCoreElementType.MAP]: 'handle',
		[DcCoreElementType.CHART]: 'handle',
		[DcCoreElementType.GRID]: 'handle',
		[DcCoreElementType.TIMELINE]: 'handle',
		[DcCoreElementType.MEDIA]: 'handle',
		[DcCoreElementType.TIMELINE]: 'handle',
		[DcCoreElementType.TIMELINE]: 'handle',
		[DcCoreElementType.RANKING]: 'handle',
	};
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToProjectEditRoute$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToProjectEditRoute),
				withLatestFrom(this.projectHomeSelector.getCanEditProject$()),
				tap(([_, canEdit]) => {
					if (!canEdit) {
						throw new Error('access denied');
					}
				}),
				tap(([action]) => {
					if (action.id !== this.projectStorage.getProjectId()) {
						throw new Error(
							$localize`:i18n=@@project.home.error.project-id-mismatch:`
						);
					}
				}),
				tap(() =>
					this.stateService.go(states.ProjectEdit, {
						projectId: `${this.projectStorage.getProjectId()}`,
					})
				),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	public navigateToElementListRoute$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToElementListRoute),
				tap((action) => {
					if (action.id !== this.projectStorage.getProjectId()) {
						throw new Error(
							$localize`:i18n=@@project.home.error.project-id-mismatch:`
						);
					}
				}),
				tap((action) => {
					switch (action.element) {
						case DcCoreElementType.CONNECTOR:
							this.stateService.go(states.ConnectorList);
							break;
						case DcCoreElementType.DEPOT:
							this.stateService.go(states.DepotList);
							break;
						case DcCoreElementType.DATABLOCK:
							this.stateService.go(states.DatablockList);
							break;
						case DcCoreElementType.ENTITY:
							this.stateService.go(states.EntityList);
							break;
						case DcCoreElementType.DASHBOARD:
							this.stateService.go(states.DashboardList);
							break;
						case DcCoreElementType.EXPOSITION:
							this.stateService.go(states.ExpositionsList);
							break;
						case DcCoreElementType.GRID:
							this.stateService.go(states.GridList);
							break;
						case DcCoreElementType.CHART:
							this.stateService.go(states.ChartList);
							break;
						case DcCoreElementType.TIMELINE:
							this.stateService.go(states.TimelineList);
							break;
						case DcCoreElementType.MAP:
							this.stateService.go(states.MapList);
							break;
						case DcCoreElementType.MEDIA:
							this.stateService.go(states.MediaList);
							break;
					}
				}),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	public navigateToElementCreateRoute$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToCreateElementRoute),
				tap((action) => {
					if (action.id !== this.projectStorage.getProjectId()) {
						throw new Error(
							$localize`:i18n=@@project.home.error.project-id-mismatch:`
						);
					}
				}),
				withLatestFrom(this.projectHomeSelector.getSelectedElement$()),
				tap(([_, element]) => {
					switch (element) {
						case DcCoreElementType.CONNECTOR:
							this.stateService.go(states.ConnectorCreate);
							break;
						case DcCoreElementType.DEPOT:
							this.stateService.go(states.DepotAngularJSCreatePage);
							break;
						case DcCoreElementType.DATABLOCK:
							this.stateService.go(states.DatablockCreate);
							break;
						case DcCoreElementType.ENTITY:
							this.stateService.go(states.EntityCreate);
							break;
						case DcCoreElementType.DASHBOARD:
							this.stateService.go(states.DashboardCreate);
							break;
						case DcCoreElementType.EXPOSITION:
							this.stateService.go(states.ExpositionCreate);
							break;
						case DcCoreElementType.GRID:
							this.stateService.go(states.GridSelect);
							break;
						case DcCoreElementType.CHART:
							this.stateService.go(states.ChartSelect);
							break;
						case DcCoreElementType.TIMELINE:
							this.stateService.go(states.TimelineCreate);
							break;
						case DcCoreElementType.MAP:
							this.stateService.go(states.MapCreate);
							break;
						case DcCoreElementType.MEDIA:
							this.stateService.go(states.MediaSelect);
							break;
					}
				}),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);
	// ##########################################################
	// Business Effects
	// ##########################################################
	public initProject$ = createEffect(() =>
		this.action$.pipe(
			ofType(initProjectHome),
			tap((action) => {
				if (action.id !== this.projectStorage.getProjectId()) {
					throw new Error(
						$localize`:i18n=@@project.home.error.project-id-mismatch:`
					);
				}
			}),
			tap((action) => {
				this.projectStorage.setModule(action.module);
				const selectedElement = this.projectStorage.getSelectedElement();
				if (this.elementModuleMapping[selectedElement] !== action.module) {
					const defaultSelection =
						action.module === 'generics'
							? DcCoreElementType.CONNECTOR
							: DcCoreElementType.DASHBOARD;
					this.projectStorage.setSelectedElement(defaultSelection);
				}
			}),
			switchMap((action) => {
				const elementType = this.projectStorage.getSelectedElement();
				return combineAll({
					project: this.projectRequester.getProject(),
					gdStat: this.projectRequester.getProjectGenericsStat(),
					hdStat: this.projectRequester.getProjectHandleStat(),
					canCreate: this.projectRequester.canCreateElement(elementType),
					traces: this.projectRequester.getElementTraces(
						BeElementTypesMapping[elementType]
					),
					element: of(elementType),
					action: of(action),
				});
			}),
			map(
				({ project, gdStat, hdStat, canCreate, traces, element, action }) => ({
					metadata: this.metadataAdapter.generateMetadata(project.metadata),
					element,
					stat: this.projectAdapter.generateProjectElementStats(
						{
							...gdStat,
							...hdStat,
						},
						action.module
					),
					traces: this.projectAdapter.generateTraceList(traces, element),
					permissions: {
						create: canCreate,
						edit: project.admin ?? false,
					},
				})
			),
			map(({ metadata, stat, traces, element, permissions }) =>
				fetchProjectDetailsSuccess({
					metadata,
					element,
					stat,
					traces,
					permissions,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public refreshTraceList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshProjectTraceList),
			withLatestFrom(this.projectHomeSelector.getSelectedElement$()),
			switchMap(([_, element]) =>
				combineLatest([
					this.projectRequester.getElementTraces(
						BeElementTypesMapping[element]
					),
					of(element),
				])
			),
			map(([response, element]) =>
				fetchElementTracesSuccess({
					traces: this.projectAdapter.generateTraceList(response, element),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public changeElementSelection$ = createEffect(() =>
		this.action$.pipe(
			ofType(changeElementSelection),
			tap((a) => this.projectStorage.setSelectedElement(a.element)),
			switchMap((a) =>
				combineLatest([
					this.projectRequester.getElementTraces(
						BeElementTypesMapping[a.element]
					),
					this.projectRequester.canCreateElement(a.element),
					of(a.element),
				])
			),
			map(([response, create, element]) =>
				fetchElementTracesAndCreate({
					traces: this.projectAdapter.generateTraceList(response, element),
					create,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly projectRequester: ProjectRequester,
		private readonly projectStorage: ProjectStorage,
		private readonly ngCommService: NgCommService,
		private readonly metadataAdapter: MetadataAdapter,
		private readonly projectAdapter: ProjectAdapter,
		private readonly projectHomeSelector: HomeSelector
	) {}
}
